import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Clock3, LogOut, ArrowLeft, Headphones, BellRing, Mail, User as UserIcon } from "lucide-react";
import { api } from "@/lib/auth";
import { useClientAuth } from "@/lib/clientAuth";
import { SITE } from "@/data/site";
import Logo from "@/components/Logo";
import NotificationSettings from "@/components/portal/NotificationSettings";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "—");

export default function ComingSoon() {
  const { user, logout } = useClientAuth();
  const nav = useNavigate();
  const [showPrefs, setShowPrefs] = useState(false);

  if (!user) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-obsidian-900">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold">Loading…</p>
      </main>
    );
  }

  const verified = !!user.email_verified;

  const resend = async () => {
    try {
      await api.post("/client/auth/resend-verification", { email: user.email });
      toast.success("Verification email sent. Please check your inbox.");
    } catch { toast.error("Could not send verification email."); }
  };

  return (
    <main className="grain relative min-h-screen overflow-hidden bg-obsidian-900 text-ivory" data-testid="coming-soon">
      {/* ambient gold glow */}
      <div className="pointer-events-none absolute -top-40 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-gold/10 blur-[120px]" />

      <header className="relative z-10 border-b border-gold/15 bg-obsidian-900/80 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Logo className="h-8" />
          <div className="flex items-center gap-3">
            <span className="hidden text-xs text-ivory/55 sm:block" data-testid="coming-soon-email">{user.email}</span>
            <button data-testid="coming-soon-signout" onClick={() => { logout(); nav("/login"); }}
              className="inline-flex items-center gap-1.5 rounded-full border border-gold/30 px-4 py-2 text-xs text-gold transition hover:bg-gold/10">
              <LogOut className="h-3.5 w-3.5" /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <div className="relative z-10 mx-auto max-w-3xl px-6 pb-24 pt-16 text-center">
        <p className="font-mono text-[11px] uppercase tracking-[0.5em] text-gold" data-testid="coming-soon-brand">Nanak Chauffeurs</p>
        <h1 className="mt-5 font-display text-4xl font-bold leading-tight text-ivory sm:text-5xl lg:text-6xl" data-testid="coming-soon-title">
          Your Private Client Portal<br />Is Coming Soon
        </h1>
        <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-ivory/70" data-testid="coming-soon-subtitle">
          Your account is ready. We're preparing an enhanced experience where you can view reservations,
          track upcoming journeys, and access your transportation history.
        </p>

        {/* verified account status */}
        <div className="mt-9 flex justify-center" data-testid="coming-soon-verified-status">
          {verified ? (
            <span className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-2 text-sm font-semibold text-gold-light">
              <ShieldCheck className="h-4 w-4" /> Verified Account
            </span>
          ) : (
            <div className="inline-flex flex-wrap items-center justify-center gap-3">
              <span className="inline-flex items-center gap-2 rounded-full border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-sm font-semibold text-amber-300">
                <Clock3 className="h-4 w-4" /> Email verification pending
              </span>
              <button onClick={resend} data-testid="coming-soon-resend" className="rounded-full border border-gold/30 px-4 py-2 text-xs text-gold transition hover:bg-gold/10">
                Resend verification email
              </button>
            </div>
          )}
        </div>

        {/* customer profile */}
        <div className="mx-auto mt-8 max-w-md rounded-2xl border border-gold/15 bg-obsidian-800/70 p-6 text-left" data-testid="coming-soon-profile">
          <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-gold">
            <UserIcon className="h-3.5 w-3.5" /> Customer Profile
          </p>
          <dl className="mt-4 space-y-2.5 text-sm">
            <div className="flex justify-between gap-4 border-b border-gold/5 pb-2"><dt className="text-ivory/45">Name</dt><dd className="text-ivory/90" data-testid="profile-name">{user.name || "—"}</dd></div>
            <div className="flex justify-between gap-4 border-b border-gold/5 pb-2"><dt className="text-ivory/45">Email</dt><dd className="text-ivory/90" data-testid="profile-email">{user.email}</dd></div>
            <div className="flex justify-between gap-4 border-b border-gold/5 pb-2"><dt className="text-ivory/45">Sign-in method</dt><dd className="text-ivory/90">{user.google_linked ? "Google" : "Email & password"}</dd></div>
            <div className="flex justify-between gap-4"><dt className="text-ivory/45">Member since</dt><dd className="text-ivory/90" data-testid="profile-created">{fmtDate(user.created_at)}</dd></div>
          </dl>
        </div>

        {/* actions */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <button data-testid="coming-soon-notifications" onClick={() => setShowPrefs((v) => !v)}
            className="inline-flex items-center gap-2 rounded-full border border-gold/30 px-5 py-2.5 text-sm text-ivory/85 transition hover:bg-gold/10">
            <BellRing className="h-4 w-4 text-gold/80" /> Notification Preferences
          </button>
          <button data-testid="coming-soon-contact" onClick={() => nav("/contact")}
            className="inline-flex items-center gap-2 rounded-full border border-gold/30 px-5 py-2.5 text-sm text-ivory/85 transition hover:bg-gold/10">
            <Headphones className="h-4 w-4 text-gold/80" /> Contact Dispatch
          </button>
          <a data-testid="coming-soon-email-link" href={`mailto:${SITE.email}`}
            className="inline-flex items-center gap-2 rounded-full border border-gold/20 px-5 py-2.5 text-sm text-ivory/70 transition hover:bg-gold/10">
            <Mail className="h-4 w-4 text-gold/70" /> {SITE.email}
          </a>
          <button data-testid="coming-soon-return" onClick={() => nav("/")}
            className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-semibold text-obsidian-900 transition hover:shadow-[0_0_28px_rgba(212,175,55,0.35)]">
            <ArrowLeft className="h-4 w-4" /> Return to Website
          </button>
        </div>

        {showPrefs && (
          <div className="mt-8 flex justify-center text-left" data-testid="coming-soon-prefs-panel">
            <NotificationSettings />
          </div>
        )}

        <p className="mt-12 font-mono text-[10px] uppercase tracking-[0.3em] text-ivory/35">
          Reservations are managed in Limo Anywhere · Portal is view-only
        </p>
      </div>
    </main>
  );
}
